/**
 * 60-second voice calibration gauntlet.
 *
 * First-time onboarding for voice scoring. The operator goes through
 * 8 short prompts ("say: four boundary"), holds V (Whisper PTT),
 * speaks the phrase, and the gauntlet scores recognition accuracy.
 * Builds trust in the system + surfaces accent mismatches BEFORE the
 * operator is mid-match.
 *
 * Founder ask 2026-05-26 ("every user can do a voice test initially
 * for 60 seconds before coding"). Auto-opens once per browser via
 * localStorage flag; replay button on the BBP voice panel re-runs it.
 *
 * Scoring rubric per prompt:
 *   - exact match (lowercased, punctuation-stripped) → 100%
 *   - alias-normalised substring match → 80%
 *   - other → 0%
 * Final score: average across all prompts. Above 7/10 = ready to
 * score; below = suggest checking mic + maybe trying a different
 * STT language setting.
 *
 * Audio archive: all 8 calibration utterances go through the normal
 * VoiceUtterance pipeline (so per-user accuracy is queryable later
 * for analytics).
 */
import { useEffect, useRef, useState } from "react";
import { Mic, Check, X, Loader2, Headphones, Radio, RotateCcw } from "lucide-react";
import { useWhisperSTT } from "../../hooks/useWhisperSTT";
import api from "../../api/axios";
import useDialogDismiss from "../../hooks/useDialogDismiss";


// 2026-06-18 — three buckets so the "done" screen can call out
// *which* layer is broken, not just an aggregate number:
//
//   silence  → confirms the hallucination guard catches no-speech audio.
//              Pass = backend rejected with diagnostic=no_speech_detected.
//   basic    → single-word / 2-word Whisper accuracy. Pass threshold ~0.9.
//              Phase 2 of the manual audit.
//   compound → longer sentences exercising the AI fallback. Pass ~0.7
//              (Whisper accuracy degrades with length; the parser/AI
//              picks up the slack — gauntlet measures just transcript
//              quality, not chip-level apply).
const PROMPTS = [
  // Phase 1 — silence/hallucination guard. The prompt asks the operator
  // to hold V WITHOUT speaking; we expect the backend to reject the
  // audio cleanly. If it doesn't (Whisper hallucinated a phrase past
  // the guard, or guard isn't deployed), this prompt fails — and that
  // tells us the user's mic/Whisper combo can produce ghost transcripts
  // mid-match. Critical to surface upfront.
  { kind: "silence", say: "Stay silent. Hold V for 1 sec, release.",
    expect: [], bucket: "silence", weight: 1.0 },

  // Phase 2 — basic accuracy (the original 8). Most cricket scoring
  // utterances boil down to 1–3 words; the regex parser handles them
  // directly so accuracy here = real-world accuracy.
  { kind: "basic", say: "four boundary",
    expect: ["four boundary", "4 boundary", "for boundary"], bucket: "basic", weight: 1.0 },
  { kind: "basic", say: "wide one",
    expect: ["wide one", "wide 1"], bucket: "basic", weight: 1.0 },
  { kind: "basic", say: "caught bowled",
    expect: ["caught bowled", "caught and bowled"], bucket: "basic", weight: 1.0 },
  { kind: "basic", say: "pitch yorker outside off",
    expect: ["pitch yorker outside off", "yorker outside off"], bucket: "basic", weight: 1.0 },
  { kind: "basic", say: "wagon deep cover",
    expect: ["wagon deep cover", "deep cover"], bucket: "basic", weight: 1.0 },
  { kind: "basic", say: "stroke cover drive",
    expect: ["stroke cover drive", "cover drive"], bucket: "basic", weight: 1.0 },
  { kind: "basic", say: "save", expect: ["save"], bucket: "basic", weight: 1.0 },
  { kind: "basic", say: "undo", expect: ["undo"], bucket: "basic", weight: 1.0 },

  // Phase 4 — compound phrases that exercise the AI fallback path in
  // real scoring. Here we score Whisper transcript fidelity only;
  // chip-level parse accuracy is measured in real match. If Whisper
  // can't hear the long form cleanly, the AI fallback can't recover.
  { kind: "compound", say: "pulled to deep midwicket for six",
    expect: ["pulled to deep midwicket for six", "pull to deep midwicket for six"],
    bucket: "compound", weight: 1.0 },
  { kind: "compound", say: "edged behind caught by keeper",
    expect: ["edged behind caught by keeper", "edge behind caught by keeper"],
    bucket: "compound", weight: 1.0 },
  { kind: "compound", say: "yorker on the stumps bowled him",
    expect: ["yorker on the stumps bowled him", "yorker on the stumps bowled"],
    bucket: "compound", weight: 1.0 },
  { kind: "compound", say: "outside off played and missed",
    expect: ["outside off played and missed", "outside off play and miss"],
    bucket: "compound", weight: 1.0 },
];


const STORAGE_KEY = "zcd_voice_calibration_done";

export function shouldShowCalibration() {
  try { return localStorage.getItem(STORAGE_KEY) !== "1"; }
  catch { return false; }
}

export function clearCalibrationFlag() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}


function norm(s) {
  return (s || "").toString().toLowerCase().replace(/[^a-z0-9 ]/g, "").replace(/\s+/g, " ").trim();
}

// 2026-06-18 audit — gauntlet was giving 0/9 even when Whisper got
// the speech ~95% right, because of trivial Whisper quirks: extra
// commas, "outside of" vs "outside off", "stroke cover dry" vs "stroke
// cover drive". Substring match alone is too strict. Adds two more
// tiers: token-overlap (how many expected words appear in the
// transcript) and prefix-overlap (matches "of" against "off" via
// shared first 3 chars). Combined with the original exact/substring
// match, this surfaces "Whisper heard 8 of 9 right" as 0.9 instead of
// 0.0 — which is what the calibration is actually measuring.
function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const prev = Array(b.length + 1).fill(0);
  const curr = Array(b.length + 1).fill(0);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }
  return prev[b.length];
}

function tokenMatches(expectedTok, transcriptTokens) {
  // 1) exact match
  if (transcriptTokens.includes(expectedTok)) return true;
  // 2) shared 3-char prefix on a token of similar length — catches
  //    "of" vs "off", "dry" vs "drive", "Wegan" vs "wagon".
  const head = expectedTok.slice(0, 3);
  for (const t of transcriptTokens) {
    if (head.length >= 2 && t.startsWith(head)) return true;
    // 3) Levenshtein ≤ 2 on tokens of length ≥ 4 (cheap typo allowance)
    if (expectedTok.length >= 4 && t.length >= 4 && levenshtein(expectedTok, t) <= 2) return true;
  }
  return false;
}

function scoreAttempt(transcript, expects) {
  const t = norm(transcript);
  if (!t) return 0;
  let best = 0;
  for (const exp of expects) {
    const e = norm(exp);
    if (!e) continue;
    if (t === e) return 1.0;
    if (t.includes(e) || e.includes(t)) { best = Math.max(best, 0.8); continue; }
    // Token-overlap tier — count how many expected tokens are matched
    // in the transcript via exact/prefix/Levenshtein.
    const ew = e.split(" ").filter(Boolean);
    const tw = t.split(" ").filter(Boolean);
    if (!ew.length) continue;
    let matched = 0;
    for (const w of ew) if (tokenMatches(w, tw)) matched += 1;
    const overlap = matched / ew.length;
    if (overlap >= 0.85) best = Math.max(best, 0.9);
    else if (overlap >= 0.6) best = Math.max(best, 0.7);
    else if (overlap >= 0.4) best = Math.max(best, 0.4);
  }
  return best;
}


export default function VoiceCalibrationGauntlet({ open, onClose, matchId = null, mandatory = false }) {
  const [idx, setIdx] = useState(0);
  const [results, setResults] = useState([]);
  const [phase, setPhase] = useState("intro"); // intro | running | done
  const liveTranscriptRef = useRef("");
  const panelRef = useRef(null);
  // Mandatory variant disables Esc / backdrop / hardware-back so the
  // operator can't bypass first-time calibration; they must complete
  // or hit the explicit Skip the parent surfaces.
  useDialogDismiss({
    open,
    onClose: mandatory ? () => {} : onClose,
    ref: panelRef,
    esc: !mandatory,
    historyBack: !mandatory,
  });
  // 2026-05-28 — track the most recent utterance_id from the transcribe
  // server so we can POST the intended phrase as an operator_correction.
  // That stamps every gauntlet utterance as a labelled (audio, intended)
  // pair, which the mine_voice_corrections command then ingests as
  // STT_ALIAS candidates per-user. Founder's "(2) calibration feeds
  // (1) alias mining" plan, 2026-05-28.
  const lastUtteranceIdRef = useRef(null);
  const lastPromptIdxRef = useRef(0);

  // Shared advance + result-record. Called from both onTranscript (real
  // transcript came back) and onRejected (silence/hallucination guard
  // fired). Each path records ONE result then auto-advances.
  const recordAndAdvance = (entry) => {
    if (phase !== "running") return;
    setResults((prev) => [...prev, entry]);
    setTimeout(() => {
      setIdx((i) => {
        if (i + 1 >= PROMPTS.length) {
          setPhase("done");
          return i;
        }
        lastPromptIdxRef.current = i + 1;
        return i + 1;
      });
    }, 1000);
  };

  const ptt = useWhisperSTT({
    onUtterance: (utteranceId) => {
      lastUtteranceIdRef.current = utteranceId || null;
    },
    onTranscript: (text) => {
      liveTranscriptRef.current = text;
      if (phase !== "running") return;
      const prompt = PROMPTS[lastPromptIdxRef.current];
      if (!prompt) return;
      // Silence-test prompt: getting a transcript back here is a FAIL
      // (the hallucination guard should have rejected it).
      if (prompt.kind === "silence") {
        recordAndAdvance({
          idx: lastPromptIdxRef.current,
          transcript: text, score: 0.0, rejected: false,
        });
        return;
      }
      const score = scoreAttempt(text, prompt.expect);
      recordAndAdvance({ idx: lastPromptIdxRef.current, transcript: text, score });
      // Stamp the intended phrase onto the utterance so the mining
      // pipeline treats this as a labelled training pair. Best-effort
      // — a failed POST doesn't block the gauntlet. Send for basic +
      // compound prompts only; silence prompt has no intended text.
      const utteranceId = lastUtteranceIdRef.current;
      if (utteranceId) {
        api.post("/api/scoring/voice/correction/", {
          utterance_id: utteranceId,
          correction: prompt.say,
        }).catch(() => { /* best-effort */ });
      }
    },
    onRejected: (info) => {
      if (phase !== "running") return;
      const prompt = PROMPTS[lastPromptIdxRef.current];
      if (!prompt) return;
      // Silence-test prompt: rejection IS the pass.
      // Any other prompt: rejection means we lost real speech to the
      // guard (false positive) — score it as a fail.
      const isSilencePrompt = prompt.kind === "silence";
      recordAndAdvance({
        idx: lastPromptIdxRef.current,
        transcript: info?.raw ? `(rejected — heard "${info.raw}")` : "(rejected — no speech)",
        score: isSilencePrompt ? 1.0 : 0.0,
        rejected: true,
      });
    },
    matchId,
  });

  useEffect(() => {
    if (open) {
      setIdx(0);
      lastPromptIdxRef.current = 0;
      setResults([]);
      setPhase("intro");
    }
  }, [open]);

  // Hotkey: hold V anywhere on the modal to record (same as scoring).
  useEffect(() => {
    if (!open || !ptt.supported || phase !== "running") return undefined;
    let pressed = false;
    const onDown = (e) => {
      if ((e.key || "").toLowerCase() !== "v") return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      if (e.repeat) return;
      pressed = true;
      e.preventDefault();
      ptt.startRecording();
    };
    const onUp = (e) => {
      if ((e.key || "").toLowerCase() !== "v") return;
      if (!pressed) return;
      pressed = false;
      e.preventDefault();
      ptt.stopRecording();
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
  }, [open, ptt.supported, phase, ptt.startRecording, ptt.stopRecording]);

  if (!open) return null;

  const finish = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    onClose?.();
  };

  const skip = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    onClose?.();
  };

  const restart = () => {
    setIdx(0);
    setResults([]);
    setPhase("running");
  };

  const totalScore = results.reduce((s, r) => s + r.score, 0);
  const avg = results.length ? (totalScore / results.length) : 0;

  // 2026-06-18 — bucketed pass/fail so the done screen can pinpoint
  // *which layer* failed (mic vs Whisper vs AI fallback), not just an
  // aggregate. Each bucket's threshold matches what the operator needs
  // for real scoring: silence guard must work (1/1), basic Whisper
  // accuracy ≥ 7/9 (founder's threshold), compound ≥ 3/4 (Whisper
  // accuracy + AI fallback can take it from there in live scoring).
  const bucketBreakdown = (() => {
    const buckets = { silence: [], basic: [], compound: [] };
    for (const r of results) {
      const p = PROMPTS[r.idx];
      if (!p || !p.bucket) continue;
      buckets[p.bucket].push(r.score);
    }
    const summarise = (arr, threshold) => {
      const total = arr.length;
      const passes = arr.filter((s) => s >= 0.8).length;
      const pct = total ? passes / total : 0;
      return { total, passes, pct, pass: total > 0 && pct >= threshold };
    };
    return {
      silence: summarise(buckets.silence, 1.0),    // 1/1 needed
      basic: summarise(buckets.basic, 0.77),       // 7/9 needed
      compound: summarise(buckets.compound, 0.75), // 3/4 needed
    };
  })();
  const allBucketsPass =
    bucketBreakdown.silence.pass
    && bucketBreakdown.basic.pass
    && bucketBreakdown.compound.pass;

  return (
    <div
      className="fixed inset-0 z-[75] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget && !mandatory) onClose?.(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Voice calibration"
        className="bg-bg-primary rounded-2xl max-w-lg w-full border border-purple-400/50 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-4 pb-3 flex items-center justify-between border-b border-bg-secondary bg-gradient-to-r from-purple-500/15 to-transparent">
          <div className="flex items-center gap-2">
            <Headphones className="w-4 h-4 text-purple-300" />
            <h3 className="text-sm font-semibold uppercase tracking-wider text-purple-200">
              Voice Calibration — 60 seconds
            </h3>
          </div>
          {!mandatory && (
            <button onClick={skip} className="text-text-secondary hover:text-text-primary text-[10px] uppercase tracking-wider">
              Skip
            </button>
          )}
          {mandatory && (
            <span className="text-purple-300/80 text-[10px] uppercase tracking-wider">Required</span>
          )}
        </div>

        <div className="px-5 py-5">
          {phase === "intro" && (
            <>
              <p className="text-sm text-text-secondary mb-3">
                Eight short phrases — hold <kbd className="px-1.5 py-0.5 rounded bg-bg-secondary border border-border-default text-[10px] font-mono">V</kbd> and read each one out, release when done.
                Takes ~60 seconds. We'll score recognition accuracy so
                you know what to expect before you start a real match.
              </p>
              <ul className="text-[11px] text-text-secondary list-disc list-inside mb-4 space-y-0.5">
                <li>Speak at your normal scoring pace.</li>
                <li>The same mic you'll use for the match.</li>
                <li>Quiet room ideally — we record what your STT will get.</li>
              </ul>
              <button
                onClick={() => setPhase("running")}
                className="w-full py-3 rounded-md bg-purple-500/30 border border-purple-400/60 text-purple-100 font-semibold uppercase tracking-wider text-xs hover:bg-purple-500/40"
              >
                Start calibration
              </button>
            </>
          )}

          {phase === "running" && (
            <>
              <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-1">
                Prompt {idx + 1} of {PROMPTS.length}
                {PROMPTS[idx].kind === "silence" && " · silence test"}
                {PROMPTS[idx].kind === "compound" && " · compound phrase"}
              </div>
              <div className="text-2xl font-semibold text-text-primary mb-1">
                {PROMPTS[idx].kind === "silence" ? (
                  <span className="text-amber-200">{PROMPTS[idx].say}</span>
                ) : (
                  <>Say: <span className="text-purple-200">"{PROMPTS[idx].say}"</span></>
                )}
              </div>
              <div className="text-[11px] text-text-secondary mb-4">
                {PROMPTS[idx].kind === "silence"
                  ? "We expect the backend to reject this as no-speech — that's the pass."
                  : <>Hold <kbd className="px-1.5 py-0.5 rounded bg-bg-secondary border border-border-default text-[10px] font-mono">V</kbd> (or the button below), speak, release.</>}
              </div>

              <button
                onMouseDown={ptt.startRecording}
                onMouseUp={ptt.stopRecording}
                onMouseLeave={() => ptt.recording && ptt.stopRecording()}
                onTouchStart={(e) => { e.preventDefault(); ptt.startRecording(); }}
                onTouchEnd={(e) => { e.preventDefault(); ptt.stopRecording(); }}
                className={`w-full flex items-center justify-center gap-2 py-4 rounded-md font-semibold uppercase tracking-wider text-sm transition-colors select-none ${
                  ptt.recording ? "bg-red-500/30 border border-red-500/60 text-red-200 animate-pulse" :
                  ptt.transcribing ? "bg-amber-500/20 border border-amber-500/50 text-amber-300" :
                  "bg-purple-500/20 border border-purple-400/50 text-purple-200 hover:bg-purple-500/30"
                }`}
              >
                {ptt.recording ? <><Mic className="w-4 h-4" /> Recording… release to score</>
                 : ptt.transcribing ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</>
                 : <><Radio className="w-4 h-4" /> Hold to talk (V)</>}
              </button>

              {/* Last attempt result if any */}
              {results.length > 0 && results[results.length - 1].idx === idx - 1 && (
                <div className="mt-3 p-2 rounded border border-bg-secondary text-[11px]">
                  <div className="text-text-secondary">previous: {PROMPTS[results[results.length - 1].idx].say}</div>
                  <div className="text-text-primary flex items-center gap-1 mt-0.5">
                    {results[results.length - 1].score >= 0.8 ?
                      <Check className="w-3.5 h-3.5 text-emerald-400" /> :
                      <X className="w-3.5 h-3.5 text-rose-400" />}
                    heard: "{results[results.length - 1].transcript}"
                  </div>
                </div>
              )}

              <div className="mt-4 flex gap-1">
                {PROMPTS.map((_, i) => (
                  <div
                    key={i}
                    className={`flex-1 h-1.5 rounded-full ${
                      i < results.length
                        ? results[i]?.score >= 0.8 ? "bg-emerald-400"
                          : results[i]?.score >= 0.4 ? "bg-amber-400"
                          : "bg-rose-400"
                        : i === idx ? "bg-purple-400/50"
                        : "bg-bg-secondary"
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          {phase === "done" && (
            <>
              <div className="text-center mb-4">
                <div className={`text-5xl font-bold mb-1 ${
                  allBucketsPass ? "text-emerald-400" :
                  avg >= 0.7 ? "text-amber-400" :
                  "text-rose-400"
                }`}>
                  {Math.round(avg * 100)}%
                </div>
                <div className="text-sm text-text-secondary">
                  {allBucketsPass
                    ? "Pass — voice coding is ready for live scoring."
                    : avg >= 0.7
                    ? "Partial pass — one layer is weak. Fix the failing row before going live."
                    : "Below the threshold. Voice may misfire on live balls — fix before scoring."}
                </div>
              </div>

              {/* Bucketed pass/fail — which layer broke, not just the
                  aggregate. Critical for the operator to know whether
                  it's their mic (silence/basic) or the AI fallback
                  (compound) that needs fixing. */}
              <div className="mb-4 grid grid-cols-3 gap-2">
                {[
                  { key: "silence",  label: "Silence guard", hint: "Did Whisper hallucination get rejected?" },
                  { key: "basic",    label: "Whisper accuracy", hint: "Single-word + short cricket terms." },
                  { key: "compound", label: "AI-fallback bed",  hint: "Long phrases for Haiku to parse." },
                ].map(({ key, label, hint }) => {
                  const b = bucketBreakdown[key];
                  return (
                    <div
                      key={key}
                      className={`p-2 rounded-md border ${
                        b.pass ? "border-emerald-500/50 bg-emerald-500/10"
                               : "border-rose-500/50 bg-rose-500/10"
                      }`}
                      title={hint}
                    >
                      <div className={`text-[10px] uppercase tracking-wider font-semibold ${
                        b.pass ? "text-emerald-300" : "text-rose-300"
                      }`}>
                        {b.pass ? "✓ Pass" : "✗ Fail"}
                      </div>
                      <div className="text-xs text-text-primary truncate">{label}</div>
                      <div className="text-[10px] text-text-secondary">
                        {b.passes}/{b.total} prompts
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Diagnostic recommendations based on the score band.
                  Founder ask 2026-05-26: give concrete next-steps
                  instead of just a number. */}
              {avg < 0.9 && (
                <div className="mb-4 p-3 rounded-md border border-purple-400/30 bg-purple-500/5 text-[11px] text-text-secondary space-y-1">
                  <div className="text-purple-200 font-semibold uppercase tracking-wider text-[10px]">
                    Suggestions
                  </div>
                  {avg < 0.5 && (
                    <ul className="list-disc list-inside space-y-0.5">
                      <li><strong className="text-text-primary">Check the mic:</strong> hold the same physical mic close (within 30cm); avoid the laptop's built-in if you have a headset.</li>
                      <li><strong className="text-text-primary">Quieter spot:</strong> stadium noise + commentary feed both interfere. Try a back room if you have one.</li>
                      <li><strong className="text-text-primary">Try the Hindi STT mode:</strong> ask the admin to switch your <code>voice_stt_language</code> to <code>hi</code> in /admin/. Some Indian accents transcribe better.</li>
                      <li><strong className="text-text-primary">Touch fallback:</strong> for this match, use the touch UI; we'll mine the recorded utterances and improve the dictionary for next time.</li>
                    </ul>
                  )}
                  {avg >= 0.5 && avg < 0.7 && (
                    <ul className="list-disc list-inside space-y-0.5">
                      <li><strong className="text-text-primary">Turn on ✨ AI fallback</strong> — it auto-applies high-confidence parses Whisper misses on natural language.</li>
                      <li><strong className="text-text-primary">Use scope prefixes</strong> — say <em>"stroke cover drive"</em> instead of just <em>"cover drive"</em> to lock the field.</li>
                      <li><strong className="text-text-primary">Slow down ~10%</strong> — STT struggles with rapid-fire compound phrases; brief pauses help.</li>
                    </ul>
                  )}
                  {avg >= 0.7 && avg < 0.9 && (
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>Enable ✨ AI fallback on the BBP voice panel — covers the long-tail phrases regex misses.</li>
                      <li>Use "no, I said X" to correct mishears in one breath — undo + re-apply.</li>
                      <li>For repetitive plays, add a voice macro in Settings → "bumrah edge" → "stroke cut, connection outside edge".</li>
                    </ul>
                  )}
                </div>
              )}

              <div className="space-y-1.5 mb-4 max-h-[40vh] overflow-y-auto">
                {results.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px] px-2 py-1.5 rounded bg-bg-secondary/40">
                    {r.score >= 0.8 ? <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> :
                     r.score >= 0.4 ? <span className="w-3.5 h-3.5 rounded-full bg-amber-400 shrink-0" /> :
                                       <X className="w-3.5 h-3.5 text-rose-400 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-text-secondary">"{PROMPTS[r.idx].say}"</div>
                      <div className="text-text-primary truncate">heard: {r.transcript || <em>(empty)</em>}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={restart}
                  className="flex-1 py-2 rounded-md bg-bg-secondary border border-border-default text-text-secondary hover:text-text-primary text-xs uppercase tracking-wider flex items-center justify-center gap-1"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Retry
                </button>
                <button
                  onClick={finish}
                  className="flex-1 py-2 rounded-md bg-purple-500/30 border border-purple-400/60 text-purple-100 text-xs uppercase tracking-wider font-semibold hover:bg-purple-500/40"
                >
                  {allBucketsPass ? "Start scoring" : "Continue anyway"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
